import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DataGrid, NGODatePicker } from "../../../common";
import CustomSelect from "../../../common/CustomSelect";
import { APIEndpoint } from "../../../utils/axios";
import { formatDateByTimezone } from "../../../utils/commonFunctions";
import moment from "moment";
import { FiTrendingUp } from "react-icons/fi";
import { UPLIFT_MAX_LIMIT } from "../../../utils/configs";


const STATUS_OPTIONS = [
  { key: 0, label: "All Status", value: "" },
  { key: 1, label: "Confirmed", value: "confirmed" },
  { key: 2, label: "Pending", value: "created" },
  { key: 3, label: "Processed", value: "completed" },
  { key: 4, label: "Cancelled", value: "cancelled" },
];

const EMPTY_STATS = {
  totalEst: 0,
  totalMod: 0,
  totalAct: 0,
  totalAdjustments: 0,
  avgEstAccuracy: 0,
  avgModAccuracy: 0,
  aiAccuracy: 0,
  plannerAccuracy: 0,
  plannerImprovement: 0,
  overrideCount: 0,
  overrideRate: 0,
  totalCount: 0,
  systemWins: 0,
  modifiedWins: 0,
  equals: 0,
  winRate: 0,
  aiWinRate: 0,
  totalExcessCost: 0,
};

function HBar({ label, value, maxValue, barColor, textColor, labelColor }) {
  const pct = maxValue > 0 ? Math.min(100, (value / maxValue) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
      <span style={{ width: "60px", fontSize: "11px", color: labelColor || "#94a3b8", flexShrink: 0 }}>
        {label}
      </span>
      <div style={{ flex: 1, background: "rgba(255,255,255,0.06)", borderRadius: "3px", height: "8px", overflow: "hidden" }}>
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: barColor,
            borderRadius: "3px",
            transition: "width 0.4s cubic-bezier(0.4,0,0.2,1)",
          }}
        />
      </div>
      <span style={{ width: "30px", fontSize: "12px", fontWeight: 600, color: textColor || barColor, textAlign: "right", flexShrink: 0 }}>
        {value}
      </span>
    </div>
  );
}

function ItemCard3Way({ item }) {
  const maxVal = Math.max(item.estimatedUplift, item.modifiedUplift, item.actualUplifted, 1);
  const adj = item.adjustments;
  const adjLabel = adj === 0 ? null : adj > 0 ? `Override (+${adj})` : `Override (${adj})`;

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "8px",
        padding: "12px 14px",
        marginBottom: "8px",
        transition: "background 0.15s",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px", gap: "8px", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "#f8fafc" }}>{item.itemName}</div>
          <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>{item.sector || item.flightNumber || item.storeName}</div>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
          {adjLabel && (
            <span style={{ background: "rgba(245,158,11,0.15)", color: "#fbbf24", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "5px", padding: "2px 9px", fontSize: "11px", fontWeight: 600 }}>
              {adjLabel}
            </span>
          )}
          {item.winner === "SYSTEM" && (
            <span style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.3)", borderRadius: "5px", padding: "2px 9px", fontSize: "11px", fontWeight: 700 }}>
              AI WIN
            </span>
          )}
          {item.winner === "MODIFIED" && (
            <span style={{ background: "rgba(16,185,129,0.15)", color: "#34d399", border: "1px solid rgba(16,185,129,0.3)", borderRadius: "5px", padding: "2px 9px", fontSize: "11px", fontWeight: 700 }}>
              PLANNER WIN
            </span>
          )}
          {item.winner === "EQUAL" && (
            <span style={{ background: "rgba(100,116,139,0.15)", color: "#94a3b8", border: "1px solid rgba(100,116,139,0.3)", borderRadius: "5px", padding: "2px 9px", fontSize: "11px", fontWeight: 600 }}>
              EQUAL
            </span>
          )}
        </div>
      </div>
      <HBar label="AI" value={item.estimatedUplift} maxValue={maxVal} barColor="#64748b" textColor="#94a3b8" />
      <HBar label="Planner" value={item.modifiedUplift} maxValue={maxVal} barColor="#60a5fa" textColor="#60a5fa" />
      <HBar label="Actual" value={item.actualUplifted} maxValue={maxVal} barColor="#1e293b" textColor="#f8fafc" labelColor="#94a3b8" />
    </div>
  );
}

function ItemCardError({ item }) {
  const maxVal = Math.max(item.estAbsError, item.modAbsError, 1);
  const adj = item.adjustments;
  const adjLabel = adj === 0 ? null : adj > 0 ? `Override (+${adj})` : `Override (${adj})`;

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "8px",
        padding: "12px 14px",
        marginBottom: "8px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px", gap: "8px", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "#f8fafc" }}>{item.itemName}</div>
          <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>{item.sector || item.flightNumber || item.storeName}</div>
        </div>
        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
          {adjLabel && (
            <span style={{ background: "rgba(245,158,11,0.15)", color: "#fbbf24", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "5px", padding: "2px 9px", fontSize: "11px", fontWeight: 600 }}>
              {adjLabel}
            </span>
          )}
          {item.winner === "SYSTEM" && (
            <span style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.3)", borderRadius: "5px", padding: "2px 9px", fontSize: "11px", fontWeight: 700 }}>
              AI WIN
            </span>
          )}
          {item.winner === "MODIFIED" && (
            <span style={{ background: "rgba(16,185,129,0.15)", color: "#34d399", border: "1px solid rgba(16,185,129,0.3)", borderRadius: "5px", padding: "2px 9px", fontSize: "11px", fontWeight: 700 }}>
              PLANNER WIN
            </span>
          )}
          {item.winner === "EQUAL" && (
            <span style={{ background: "rgba(100,116,139,0.15)", color: "#94a3b8", border: "1px solid rgba(100,116,139,0.3)", borderRadius: "5px", padding: "2px 9px", fontSize: "11px", fontWeight: 600 }}>
              EQUAL
            </span>
          )}
        </div>
      </div>
      <HBar label="AI Err" value={item.estAbsError} maxValue={maxVal} barColor="#f59e0b" textColor="#f59e0b" />
      <HBar label="Plnr Err" value={item.modAbsError} maxValue={maxVal} barColor="#60a5fa" textColor="#60a5fa" />
    </div>
  );
}



export const UpliftComparisonPage = () => {
  const { t, ready } = useTranslation();
  const [sectorSearch] = useState("");
  const [rawUplifts, setRawUplifts] = useState([]);
  const [flightDate, setFlightDate] = useState(new Date());
  const [locationFilter, setLocationFilter] = useState("");
  const [warehouseIdFilter, setWarehouseIdFilter] = useState("");
  const [storeIdFilter, setStoreIdFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [sectorOptions, setSectorOptions] = useState([]);

  // Comparison panel state
  const [comparisonMode, setComparisonMode] = useState("3way"); // '3way' | 'error'
  const [overriddenOnly, setOverriddenOnly] = useState(false);

  const buildFilterPayload = useCallback(() => {
    const filters = [];
    const filterConditions = [
      { condition: "location", filter: { location: locationFilter || null } },
      {
        condition: "warehouseId",
        filter: { warehouseId: warehouseIdFilter || null },
      },
      { condition: "storeId", filter: { storeId: storeIdFilter || null } },
      {
        condition: "flightDate",
        filter: {
          flightDate: flightDate ? formatDateByTimezone(flightDate) : null,
        },
      },
      { condition: "status", filter: { status: statusFilter || null } },
    ];

    filterConditions.forEach(({ filter }) => {
      const value = Object.values(filter)[0];
      if (value !== null && value !== undefined && value !== "") {
        filters.push(filter);
      }
    });

    return filters;
  }, [
    flightDate,
    locationFilter,
    statusFilter,
    storeIdFilter,
    warehouseIdFilter,
  ]);

  const fetchWarehouseOptions = useCallback(async () => {
    try {
      const resp = await APIEndpoint.get("warehouses");
      const mapped = [
        { key: 0, label: "Select Warehouse", value: "" },
        ...(resp?.result || []).map((fieldValue) => ({
          key: fieldValue.id,
          label: fieldValue.value,
          value: fieldValue.id,
        })),
      ];
      setWarehouseOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch warehouses:", err);
    }
  }, []);

  const fetchAirportOptions = useCallback(async () => {
    try {
      const resp = await APIEndpoint.get("airports");
      const mapped = [
        { key: 0, label: "Select Location", value: "" },
        ...(resp?.items || []).map((fieldValue) => ({
          key: fieldValue.id,
          label: fieldValue.locationCode,
          value: fieldValue.locationCode,
        })),
      ];
      setLocationOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch airports:", err);
    }
  }, []);

  const fetchSectorOptions = useCallback(async () => {
    try {
      const resp = await APIEndpoint.get("stores");
      const mapped = (resp?.result || []).map((data) => ({
        key: data.id,
        label: `${data.flightNumber}-${data.origin}-${data.destination}`,
        value: data.id,
      }));
      setSectorOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch sectors:", err);
    }
  }, []);

  useEffect(() => {
    fetchWarehouseOptions();
    fetchAirportOptions();
    fetchSectorOptions();
  }, [fetchAirportOptions, fetchSectorOptions, fetchWarehouseOptions]);

  // Data fetch
  const fetchUpliftsData = useCallback(async () => {
    try {
      const requestBody = {
        action: 4,
        filters: buildFilterPayload(),
        limit: UPLIFT_MAX_LIMIT || 100,
        page: 1,
        sort: [{ field: "targetDate", order: "desc" }],
      };
      const res = await APIEndpoint.post(
        "uplifts/process",
        JSON.stringify(requestBody),
      );
      const itemsArr = res?.items || (Array.isArray(res) ? res : []);
      setRawUplifts(itemsArr);
    } catch (err) {
      console.error("Failed to fetch uplifts:", err);
    }
  }, [buildFilterPayload]);

  useEffect(() => {
    fetchUpliftsData();
  }, [fetchUpliftsData]);

 
  const apiComparisonItems = useMemo(() => {
    const flatList = [];

    rawUplifts.forEach((uplift, idx) => {
      const storeName = uplift.storeName || `Store-${idx}`;

      const sectorsArr = Array.isArray(uplift.sectors) && uplift.sectors.length > 0
        ? uplift.sectors
        : [storeName];
      const sector = sectorsArr.join(", ");
      const flightNumber = sectorsArr[0] || storeName;

      const departureDate = uplift.targetDate
        ? moment(uplift.targetDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD");

      const whName = uplift.warehouse?.warehouseName || "Hub Warehouse";
      const location = uplift.location || "";
      const createdBy = uplift.upliftedBy || "System";
      const confirmedBy = uplift.confirmedBy || null;
      const status = uplift.status || "";

      if (!Array.isArray(uplift.items) || uplift.items.length === 0) return;

      uplift.items.forEach((item, itemIdx) => {
        const bob = Number(item.bob || 0);
        const corporate = Number(item.corporate || 0);
        const preBooked = Number(item.webMeal || 0);
        const adjustments = Number(item.others ?? 0);
        const estimatedUplift = Number(
          item.estimatedQuantity ?? item.estimated ?? (bob + corporate + preBooked),
        );
        const modifiedUplift = Number(item.total ?? 0);
        const actualUplifted = Number(
          item.confirmedByCateringOfficer?.total ??
          item.confirmedByCateringOfficerValue ??
          item.verifiedByCateringOfficer?.total ??
          item.verifiedByCateringOfficerValue ??
          item.scannedByWarehouseOfficer?.total ??
          item.scannedByWarehouseOfficerValue ??
          item.total ??
          0
        );

        flatList.push({
          id: `${uplift.id || idx}_${item.itemId || itemIdx}`,
          upliftId: uplift.id,
          storeName,
          flightNumber,
          sector,
          departureDate,
          warehouse: whName,
          location,
          createdBy,
          confirmedBy,
          status,
          itemCode: item.itemCode || `ITEM-${itemIdx + 1}`,
          itemName: item.itemName || "Standard Item",
          category: item.category || item.unit || "General",
          unitCost: Number(item.unitCost || item.cost || 120),
          bob,
          corporate,
          preBooked,
          adjustments,
          estimatedUplift,
          modifiedUplift,
          actualUplifted,
        });
      });
    });

    return flatList;
  }, [rawUplifts]);


  const processedData = useMemo(() => {
    const whLower = locationFilter ? locationFilter.toLowerCase() : null;
    const searchLower = sectorSearch ? sectorSearch.toLowerCase() : null;

    return apiComparisonItems
      .filter((item) => {
        if (
          whLower &&
          !item.location.toLowerCase().includes(whLower) &&
          !item.warehouse.toLowerCase().includes(whLower)
        )
          return false;
        if (
          searchLower &&
          !item.flightNumber.toLowerCase().includes(searchLower) &&
          !item.sector.toLowerCase().includes(searchLower) &&
          !item.storeName.toLowerCase().includes(searchLower) &&
          !item.itemName.toLowerCase().includes(searchLower)
        )
          return false;
        return true;
      })
      .map((item) => {
        const estDiff = item.estimatedUplift - item.actualUplifted;
        const modDiff = item.modifiedUplift - item.actualUplifted;
        const estAbsError = Math.abs(estDiff);
        const modAbsError = Math.abs(modDiff);
        const actual = item.actualUplifted || 1;

        const estAccuracy = parseFloat(
          Math.max(0, 100 - (estAbsError / actual) * 100).toFixed(1),
        );
        const modAccuracy = parseFloat(
          Math.max(0, 100 - (modAbsError / actual) * 100).toFixed(1),
        );

        let winner = "EQUAL";
        if (estAbsError < modAbsError) winner = "SYSTEM";
        else if (modAbsError < estAbsError) winner = "MODIFIED";

        const estCostVariance = estDiff * item.unitCost;
        const modCostVariance = modDiff * item.unitCost;
        const manualWasteCost = Math.max(0, modCostVariance - estCostVariance);

        return {
          ...item,
          estDiff,
          modDiff,
          estAbsError,
          modAbsError,
          estAccuracy,
          modAccuracy,
          winner,
          estCostVariance,
          modCostVariance,
          manualWasteCost,
        };
      });
  }, [apiComparisonItems, locationFilter, sectorSearch]);

  // Backend (uplifts/comparison) already guarantees others > 0 for every item.
  // However, when falling back to uplifts/process, we receive all items,
  // so we filter client-side to find modifiedData (where adjustments are non-zero).
  const modifiedData = useMemo(
    () => processedData.filter((d) => d.adjustments !== 0),
    [processedData],
  );

  const comparisonDisplayItems = useMemo(
    () => (overriddenOnly ? modifiedData : processedData),
    [overriddenOnly, modifiedData, processedData],
  );


  const stats = useMemo(() => {
    if (!processedData.length) return EMPTY_STATS;

    const acc = processedData.reduce(
      (a, d) => {
        a.totalEst += d.estimatedUplift;
        a.totalMod += d.modifiedUplift;
        a.totalAct += d.actualUplifted;
        a.totalAdjustments += d.adjustments;
        a.sumEstAcc += d.estAccuracy;
        a.sumModAcc += d.modAccuracy;
        a.totalExcessCost += Math.max(0, d.manualWasteCost);
        if (d.winner === "SYSTEM") a.systemWins++;
        else if (d.winner === "MODIFIED") a.modifiedWins++;
        else a.equals++;
        return a;
      },
      {
        totalEst: 0,
        totalMod: 0,
        totalAct: 0,
        totalAdjustments: 0,
        sumEstAcc: 0,
        sumModAcc: 0,
        totalExcessCost: 0,
        systemWins: 0,
        modifiedWins: 0,
        equals: 0,
      },
    );

    const n = processedData.length;
    const aiAccuracy = parseFloat((acc.sumEstAcc / n).toFixed(1));
    const plannerAccuracy = parseFloat((acc.sumModAcc / n).toFixed(1));
    const plannerImprovement = parseFloat((plannerAccuracy - aiAccuracy).toFixed(1));
    // All items from uplifts/comparison have others > 0 (guaranteed by backend).
    // But when using uplifts/process, we filter by adjustments !== 0 to find the overrideCount.
    const overrideCount = processedData.filter((d) => d.adjustments !== 0).length;
    const overrideRate = parseFloat(((overrideCount / n) * 100).toFixed(1));
    const aiWinRate = parseFloat(((acc.systemWins / n) * 100).toFixed(1));
    return {
      totalEst: acc.totalEst,
      totalMod: acc.totalMod,
      totalAct: acc.totalAct,
      totalAdjustments: acc.totalAdjustments,
      avgEstAccuracy: aiAccuracy,
      avgModAccuracy: plannerAccuracy,
      aiAccuracy,
      plannerAccuracy,
      plannerImprovement,
      overrideCount,
      overrideRate,
      totalCount: n,
      systemWins: acc.systemWins,
      modifiedWins: acc.modifiedWins,
      equals: acc.equals,
      winRate: ((acc.systemWins / n) * 100).toFixed(1),
      aiWinRate,
      totalExcessCost: acc.totalExcessCost,
    };
  }, [processedData]);


  const columns = useMemo(
    () => [
      {
        field: "itemName",
        headerName: "Item / SKU",
        width: 180,
        autoHeight: true,
        cellRenderer: (params) => (
          <div style={{ lineHeight: "1.4", paddingTop: "6px", paddingBottom: "6px" }}>
            <div style={{ fontWeight: 600, color: "#f1f5f9", fontSize: "13px" }}>
              {params.data.itemName}
            </div>
            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
              {params.data.itemCode}
              {params.data.itemCode && params.data.itemCategory ? " • " : ""}
              {params.data.itemCategory}
            </div>
          </div>
        ),
      },
      {
        field: "storeName",
        headerName: "Flight",
        width: 120,
        cellRenderer: (params) => (
          <span>
            {params.data.flightNumber || params.data.storeName || params.data.sector}
          </span>
        ),
      },
      {
        field: "estimatedUplift",
        headerName: "AI Est",
        width: 80,
        cellRenderer: (params) => (
          <span>
            {params.value}
          </span>
        ),
      },
      {
        field: "modifiedUplift",
        headerName: "Staff Est",
        width: 90,
        cellRenderer: (params) => (
          <span >
            {params.value}
          </span>
        ),
      },
      {
        field: "actualUplifted",
        headerName: "Actual",
        width: 80,
        cellRenderer: (params) => (
          <span>
            {params.value}
          </span>
        ),
      },
      {
        field: "estDiff",
        headerName: "Change",
        width: 80,
        cellRenderer: (params) => {
          const val = params.data.modDiff ?? params.value;
          const color = val === 0 ? "#64748b" : val > 0 ? "#34d399" : "#f87171";
          const label = val === 0 ? "0" : val > 0 ? `+${val}` : `${val}`;
          return (
            <span style={{ fontWeight: 700, color, fontSize: "13px" }}>{label}</span>
          );
        },
      },
      {
        field: "winner",
        headerName: "Winner",
        width: 130,
        cellRenderer: (params) => {
          const isModified = params.value === "MODIFIED";
          const isSystem   = params.value === "SYSTEM";
          const label  = isModified ? "PLANNER WIN" : isSystem ? "AI WIN" : "EQUAL";
          const bg     = isModified ? "rgba(245,158,11,0.15)" : isSystem ? "rgba(59,130,246,0.15)" : "rgba(100,116,139,0.12)";
          const color  = isModified ? "#fbbf24" : isSystem ? "#60a5fa" : "#94a3b8";
          const border = isModified ? "rgba(245,158,11,0.4)" : isSystem ? "rgba(59,130,246,0.4)" : "rgba(100,116,139,0.3)";
          return (
            <span style={{ background: bg, color, border: `1px solid ${border}`, padding: "2px 8px", borderRadius: "4px", }}>
              {label}
            </span>
          );
        },
      },
      {
        field: "estDiff",
        headerName: "AI Error",
        width: 90,
        cellRenderer: (params) => {
          const val = Math.abs(params.value ?? 0);
          const color = val === 0 ? "#34d399" : val <= 3 ? "#fbbf24" : "#f87171";
          return (
            <span style={{ fontWeight: 600, color, fontSize: "13px" }}>{val}</span>
          );
        },
      },
      {
        field: "modDiff",
        headerName: "Staff Err",
        width: 95,
        cellRenderer: (params) => {
          const val = Math.abs(params.value ?? 0);
          const color = val === 0 ? "#34d399" : val <= 3 ? "#fbbf24" : "#f87171";
          return (
            <span style={{ fontWeight: 600, color, fontSize: "13px" }}>{val}</span>
          );
        },
      }
    ],
    [],
  );


  return (
    <div
      className="device-page op-aircraft-container flexCol full-flex ag-theme-alpine-dark"
      style={{ overflowY: "auto", padding: "16px 20px", gap: "16px" }}
    >
      <div
        className="flexRow width100 justifyContentSpaceBetween alignItemsCenter flexWrap"
        style={{
          background: "#1e293b",
          padding: "12px 16px",
          borderRadius: "8px",
          border: "1px solid #334155",
          gap: "12px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 600,
            color: "#f8fafc",
          }}
        >
          Uplift Comparison
        </h3>

        <div className="flexRow alignItemsCenter justifyContentCenter gap-2">
          <CustomSelect
            options={sectorOptions}
            value={sectorOptions.filter(
              (option) => option.value === storeIdFilter,
            )}
            onChange={(selected) => setStoreIdFilter(selected?.value || "")}
            placeHolder={"Search Sector"}
            isClearable
            isSearchable
            className="selectMenu-meals"
          />

          <NGODatePicker
            style={{ width: "140px", border: "0.7px solid #fff" }}
            value={flightDate}
            name="flightDate"
            dateFormat="dd/MM/yyyy"
            className="input-margin"
            placeholder={t("datePickerPlaceholder")}
            onChange={(event) => {
              const nextDate = event?.target?.value;
              const safeDate =
                nextDate instanceof Date && !Number.isNaN(nextDate.getTime())
                  ? nextDate
                  : new Date();
              setFlightDate(safeDate);
            }}
          />

          <CustomSelect
            options={locationOptions}
            placeHolder={"Search location"}
            value={locationOptions.filter(
              (option) => option.value === locationFilter,
            )}
            onChange={(selected) => setLocationFilter(selected?.value || "")}
          />

          <CustomSelect
            options={warehouseOptions}
            placeHolder={"Search warehouse"}
            value={warehouseOptions.filter(
              (option) => option.value === warehouseIdFilter,
            )}
            onChange={(selected) => setWarehouseIdFilter(selected?.value || "")}
          />

          <CustomSelect
            options={STATUS_OPTIONS}
            placeHolder={"Search Status"}
            value={STATUS_OPTIONS.filter(
              (option) => option.value === statusFilter,
            )}
            onChange={(selected) => setStatusFilter(selected?.value || "")}
          />
        </div>
      </div>

      <div
        className="width100"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: "12px",
        }}
      >
        {[
          {
            id: "ai-accuracy",
            icon: "◎",
            iconColor: "#10b981",
            label: "AI ACCURACY",
            value: `${stats.aiAccuracy}%`,
            sub: "AI Estimate vs Actual",
            valueColor: "#34d399",
            borderColor: "#10b981",
          },
          {
            id: "planner-accuracy",
            icon: "👤",
            iconColor: "#7c3aed",
            label: "PLANNER ACCURACY",
            value: `${stats.plannerAccuracy}%`,
            sub: "Planner Estimate vs Actual",
            valueColor: "#c4b5fd",
            borderColor: "#7c3aed",
          },
          {
            id: "planner-improvement",
            icon: "↑",
            iconColor: "#10b981",
            label: "PLANNER IMPROVEMENT",
            value: stats.plannerImprovement >= 0
              ? `+${stats.plannerImprovement}%`
              : `${stats.plannerImprovement}%`,
            sub: "Value added by override",
            valueColor: stats.plannerImprovement >= 0 ? "#34d399" : "#f87171",
            borderColor: stats.plannerImprovement >= 0 ? "#10b981" : "#ef4444",
          },
          {
            id: "override-rate",
            icon: "✏",
            iconColor: "#3b82f6",
            label: "OVERRIDE RATE",
            value: `${stats.overrideRate}%`,
            sub: `${stats.overrideCount} of ${stats.totalCount} items modified`,
            valueColor: "#60a5fa",
            borderColor: "#3b82f6",
          },
          {
            id: "ai-win-rate",
            icon: "★",
            iconColor: "#94a3b8",
            label: "AI WIN RATE",
            value: `${stats.aiWinRate}%`,
            sub: "AI closer than Planner",
            valueColor: "#f8fafc",
            borderColor: "#475569",
          },
        ].map(({ id, icon, iconColor, label, value, sub, valueColor, borderColor }) => (
          <div
            key={id}
            style={{
              background: "#1e293b",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid #334155",
              borderLeft: `4px solid ${borderColor}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
              <span style={{ fontSize: "13px", color: iconColor }}>{icon}</span>
              <span style={{ fontSize: "10px", fontWeight: 700, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                {label}
              </span>
              <span style={{ fontSize: "11px", color: "#475569", marginLeft: "auto" }}>ⓘ</span>
            </div>
            <div style={{ fontSize: "26px", fontWeight: 700, color: valueColor, lineHeight: 1.1 }}>
              {value}
            </div>
            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "5px", fontWeight: 400 }}>
              {sub}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          background: "#1e293b",
          padding: "16px",
          borderRadius: "8px",
          border: "1px solid #334155",
        }}
      >
        <div
          className="flexRow alignItemsCenter justifyContentSpaceBetween flexWrap"
          style={{ marginBottom: "14px", gap: "10px" }}
        >
          <div className="flexRow alignItemsCenter gap-2" style={{ gap: "8px" }}>
            <FiTrendingUp color="#60a5fa" size={16} />
            <h4
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: 600,
                color: "#f8fafc",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              Top Performance Comparison (by Item)
            </h4>
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
              — AI, Planner, and Actual ground truth
            </span>
          </div>

          <div className="flexRow alignItemsCenter" style={{ gap: "10px" }}>
            <div
              style={{
                display: "flex",
                border: "1px solid #334155",
                borderRadius: "7px",
                overflow: "hidden",
              }}
            >
              {[
                { id: "3way", label: "3-Way Units" },
                { id: "error", label: "Error Variance" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setComparisonMode(id)}
                  style={{
                    padding: "6px 14px",
                    fontSize: "12px",
                    fontWeight: comparisonMode === id ? 700 : 500,
                    border: "none",
                    cursor: "pointer",
                    background: comparisonMode === id ? "#2563eb" : "transparent",
                    color: comparisonMode === id ? "#fff" : "#94a3b8",
                    transition: "all 0.15s",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "#94a3b8",
                cursor: "pointer",
                border: "1px solid #334155",
                borderRadius: "7px",
                padding: "5px 12px",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={overriddenOnly}
                onChange={(e) => setOverriddenOnly(e.target.checked)}
                style={{ accentColor: "#2563eb", width: "13px", height: "13px", cursor: "pointer" }}
              />
              Overridden Only ({modifiedData.length})
            </label>
          </div>
        </div>

        <div
          className="flexRow alignItemsCenter"
          style={{ gap: "16px", marginBottom: "12px", paddingBottom: "10px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          {comparisonMode === "3way" ? (
            <>
              <LegendDot color="#64748b" label="AI Estimate" />
              <LegendDot color="#60a5fa" label="Planner Modified" />
              <LegendDot color="#475569" label="Actual Ground Truth" />
            </>
          ) : (
            <>
              <LegendDot color="#f59e0b" label="AI Absolute Error" />
              <LegendDot color="#60a5fa" label="Planner Absolute Error" />
            </>
          )}
        </div>

        <div style={{ maxHeight: "320px", overflowY: "auto", paddingRight: "4px" }}>
          {comparisonDisplayItems.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#64748b", fontSize: "13px" }}>
              No items match the current filters.
            </div>
          ) : (
            comparisonDisplayItems.map((item) =>
              comparisonMode === "3way" ? (
                <ItemCard3Way key={item.id} item={item} />
              ) : (
                <ItemCardError key={item.id} item={item} />
              )
            )
          )}
        </div>
      </div>

      <div className="flexCol full-flex">
        <div
          className="flexRow alignItemsCenter justifyContentSpaceBetween"
          style={{ marginBottom: "8px" }}
        >
          <div className="flexRow alignItemsCenter" style={{ gap: "8px" }}>
            <span
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                background: "#f59e0b",
                display: "inline-block",
              }}
            />
            <span
              style={{ fontSize: "14px", fontWeight: 600, color: "#f8fafc" }}
            >
              Planner Override & Decision Accuracy Matrix
            </span>
          </div>
        </div>

        <div style={{ height: "420px", width: "100%" }}>
          <DataGrid
            columns={columns}
            rowData={modifiedData}
            rowModelType="clientSide"
            enableGridSearch={false}
            showDefaultFilters={false}
            pagination={{
              enabled: true,
              pageSize: 20,
              defaultPageSize: 20,
              showPageSizeSelector: true,
              pageSizes: [20, 50, 100],
              position: "top",
            }}
            columnResize
            className="full-flex"
          />
        </div>
      </div>
      {!ready && <div className="overlay_hide sub"></div>}
    </div>
  );
};

function LegendDot({ color, label }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#94a3b8" }}>
      <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />
      {label}
    </span>
  );
}
